import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from './firebase';

class StorageService {
  private storage = storage;

  /**
   * Sanitize email for use in file path
   * "user@example.com" → "user_example_com"
   */
  private sanitizeEmail(email: string): string {
    return email.replace(/[@.]/g, '_');
  }

  /**
   * Upload customization image/file to Firebase Storage
   * @param file - File to upload
   * @param customFileName - Optional custom filename
   * @returns Download URL or null if upload fails
   */
  async uploadCustomizationImage(
    file: File,
    customFileName?: string
  ): Promise<string | null> {
    try {
      if (!this.storage) {
        throw new Error('Firebase storage is not configured');
      }

      // Get current user email
      const userEmail = auth?.currentUser?.email;
      if (!userEmail) {
        throw new Error('User must be logged in to upload files');
      }

      // Sanitize email for path
      const sanitizedEmail = this.sanitizeEmail(userEmail);

      // Generate filename with timestamp
      const timestamp = Date.now();
      const fileName = customFileName || file.name;
      const finalFileName = `${timestamp}_${fileName}`;

      // Create storage reference
      const storageRef = ref(
        this.storage,
        `customizations/${sanitizedEmail}/${finalFileName}`
      );

      console.log(`📤 Uploading customization image to: ${storageRef.fullPath}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      console.log(`✅ Upload successful! URL: ${downloadURL}`);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading customization image:', error);
      return null;
    }
  }

  /**
   * Upload multiple customization images
   * @param files - Array of files to upload
   * @returns Array of download URLs
   */
  async uploadMultipleCustomizationImages(files: File[]): Promise<string[]> {
    const uploadPromises = files.map((file) => this.uploadCustomizationImage(file));
    const results = await Promise.all(uploadPromises);
    return results.filter((url): url is string => url !== null);
  }

  /**
   * Get all customization images for the current user
   * Note: This requires listing storage which may not be enabled by default
   */
  async getUserCustomizationImages(): Promise<string[]> {
    try {
      if (!this.storage) {
        return [];
      }

      const userEmail = auth?.currentUser?.email;
      if (!userEmail) {
        return [];
      }

      const sanitizedEmail = this.sanitizeEmail(userEmail);
      const userRef = ref(this.storage, `customizations/${sanitizedEmail}`);

      // Note: listAll() requires Firebase Storage list permission
      // This is a placeholder - full implementation would need proper permissions
      console.log(`📁 User customization folder: ${userRef.fullPath}`);

      return [];
    } catch (error) {
      console.error('Error getting user images:', error);
      return [];
    }
  }
}

export const storageService = new StorageService();
